import React, { useState, useEffect, useRef, useCallback } from "react";

import { Box } from "@mui/material";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()(() => ({
  content: {
    /* for Internet Explorer and Edge */
    msOverflowStyle: "none",
    overflow: "auto",
    /* for Firefox */
    scrollbarWidth: "none",
    /* for Chrome, Safari, and Opera */
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  scrollbarTrack: {
    backgroundColor: "#FAFAFA",
    position: "absolute",
    bottom: "0",
    top: "0",
    cursor: "pointer",
    width: "100%",
  },
}));

interface ScrollbarProps extends React.ComponentPropsWithoutRef<"div"> {
  children: React.ReactNode;
}

const MealPlannerContainer: React.FC<ScrollbarProps> = ({
  children,
  ...props
}) => {
  const { classes } = useStyles();

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const observer = useRef<ResizeObserver | null>(null);

  const [thumbWidth, setThumbWidth] = useState(24);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);
  const [scrollStartPosition, setScrollStartPosition] =
    useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isScrollable, setIsScrollable] = useState(true);

  const handleScrollbarThumbResize = (
    ref: HTMLDivElement,
    trackSize: number
  ) => {
    const { clientWidth, scrollWidth } = ref;
    setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 24));
    setIsScrollable(scrollWidth > clientWidth);
  };

  const handleScrollbarTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const { current: trackCurrent } = scrollTrackRef;
      const { current: contentCurrent } = contentRef;

      if (trackCurrent && contentCurrent) {
        const { clientX } = e;
        const rect = trackCurrent.getBoundingClientRect();
        const trackLeft = rect.left;
        const thumbOffset = -(thumbWidth / 2);
        const clickRatio =
          (clientX - trackLeft + thumbOffset) / trackCurrent.clientWidth;
        const scrollAmount = Math.floor(
          clickRatio * contentCurrent.scrollWidth
        );
        contentCurrent.scrollTo({
          left: scrollAmount,
          behavior: "smooth",
        });
      }
    },
    [thumbWidth]
  );

  const handleScrollbarThumbPosition = useCallback(() => {
    const isRefNull =
      !contentRef.current || !scrollTrackRef.current || !scrollThumbRef.current;

    if (isRefNull) return;

    const { scrollLeft: contentLeft, scrollWidth: contentWidth } =
      contentRef.current;
    const { clientWidth: trackWidth } = scrollTrackRef.current;

    const thumb = scrollThumbRef.current;
    let thumbLeftSpace = (contentLeft / contentWidth) * trackWidth;
    thumbLeftSpace = Math.min(thumbLeftSpace, trackWidth - thumbWidth);
    thumb.style.left = `${thumbLeftSpace}px`;
  }, [thumbWidth]);

  const handleScrollbarThumbMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setScrollStartPosition(e.clientX);
      if (contentRef.current) {
        setInitialScrollLeft(contentRef.current.scrollLeft);
      }
      setIsDragging(true);
    },
    []
  );

  const handleScrollbarThumbMouseUp = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isDragging) setIsDragging(false);
    },
    [isDragging]
  );

  const handleScrollbarThumbMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isDragging && contentRef.current) {
        const {
          scrollWidth: contentScrollWidth,
          offsetWidth: contentOffsetWidth,
        } = contentRef.current;

        const deltaX =
          (e.clientX - (scrollStartPosition ?? 0)) *
          (contentOffsetWidth / thumbWidth);
        const newScrollLeft = Math.min(
          initialScrollLeft + deltaX,
          contentScrollWidth - contentOffsetWidth
        );

        contentRef.current.scrollLeft = newScrollLeft;
      }
    },
    [isDragging, scrollStartPosition, thumbWidth, initialScrollLeft]
  );

  const handleShowScrollbar = useCallback((ref: HTMLDivElement) => {
    setIsScrollable(ref.scrollWidth > ref.clientWidth);
  }, []);

  // useEffect to observe size changes in the content element and adjust the scrollbar thumb size accordingly.
  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const { clientWidth: trackSize } = scrollTrackRef.current;
      const ref = contentRef.current;

      // Initialize a ResizeObserver to monitor size changes of the content element
      observer.current = new ResizeObserver(() => {
        handleScrollbarThumbResize(ref, trackSize);
      });

      // Start observing the content element
      observer.current.observe(ref);

      // Clean-up: stop observing the content element when the component unmounts or dependencies change
      return () => observer.current?.unobserve(ref);
    }
  }, [handleScrollbarThumbResize]);

  // useEffect to determine if the content is scrollable and handle window resize events.
  useEffect(() => {
    if (contentRef.current) {
      const ref = contentRef.current;

      // Set initial scrollable state based on content size
      setIsScrollable(ref.scrollWidth > ref.clientWidth);

      // Add a resize event listener to handle showing the scrollbar on window resize
      const resizeHandler = () => handleShowScrollbar(ref);
      window.addEventListener("resize", resizeHandler);

      // Clean-up: remove the resize event listener when the component unmounts
      return () => {
        window.removeEventListener("resize", resizeHandler);
      };
    }
  }, []);

  // useEffect to handle the scroll position of the content element
  useEffect(() => {
    if (contentRef.current) {
      const ref = contentRef.current;

      // Set initial scrollable state based on content size
      setIsScrollable(ref.scrollWidth > ref.clientWidth);

      // Add a scroll event listener to update the scrollbar thumb position
      ref.addEventListener("scroll", handleScrollbarThumbPosition);

      // Clean-up: remove the scroll event listener when the component unmounts or dependencies change
      return () => {
        ref.removeEventListener("scroll", handleScrollbarThumbPosition);
      };
    }
  }, [handleScrollbarThumbPosition]);

  // Cleans up event listeners
  useEffect(() => {
    document.addEventListener("mousemove", handleScrollbarThumbMouseMove);
    document.addEventListener("mouseup", handleScrollbarThumbMouseUp);
    document.addEventListener("mouseleave", handleScrollbarThumbMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleScrollbarThumbMouseMove);
      document.removeEventListener("mouseup", handleScrollbarThumbMouseUp);
      document.removeEventListener("mouseleave", handleScrollbarThumbMouseUp);
    };
  }, [handleScrollbarThumbMouseMove, handleScrollbarThumbMouseUp]);

  return (
    <Box border="2px solid #FAFAFA">
      {isScrollable && (
        <Box display="flex" height={16} position="relative" width="100%">
          {/* Scrollbar track */}
          <Box
            className={classes.scrollbarTrack}
            ref={scrollTrackRef}
            onClick={handleScrollbarTrackClick}
          />
          {/* Scrollbar thumb */}
          <Box
            bgcolor="#D6D6D6"
            position="absolute"
            height="100%"
            width="24px"
            ref={scrollThumbRef}
            onMouseDown={handleScrollbarThumbMouseDown}
            style={{
              width: `${thumbWidth}px`,
              cursor: isDragging ? "grabbing" : "grab",
            }}
          />
        </Box>
      )}

      <Box className={classes.content} ref={contentRef} {...props}>
        {children}
      </Box>
    </Box>
  );
};

export default MealPlannerContainer;
